import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Alert,
  InputAdornment,
  IconButton,
  Fade,
  Stepper,
  Step,
  StepLabel,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress
} from '@mui/material';
import {
  Restaurant as RestaurantIcon,
  Visibility,
  VisibilityOff,
  PersonAdd as PersonAddIcon,
  ArrowBack as ArrowBackIcon,
  Email,
  Lock,
  Person,
  Phone,
  Store,
  LocationOn,
  Business,
  AttachMoney
} from '@mui/icons-material';

const steps = ['Account Info', 'Shop Details', 'Confirm'];

const Signup = ({ onSignup }) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    shop_name: '',
    shop_address: '',
    business_type: 'restaurant',
    currency: 'PKR',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const validateStep = () => {
    switch (activeStep) {
      case 0:
        if (!formData.name.trim()) return 'Name is required';
        if (!formData.email.trim()) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) return 'Please enter a valid email address';
        if (!formData.password) return 'Password is required';
        if (formData.password.length < 6) return 'Password must be at least 6 characters';
        if (formData.password !== formData.confirmPassword) return 'Passwords do not match';
        return '';
      case 1:
        if (!formData.shop_name.trim()) return 'Shop name is required';
        return '';
      default:
        return '';
    }
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) {
      setError(err);
      return;
    }
    setError('');
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError('');
  };

  // ==================== CHECK IF EMAIL EXISTS ====================
  const checkEmailExists = async (email) => {
    try {
      // Check in Electron API
      if (window.electronAPI?.getUserByEmail) {
        const result = await window.electronAPI.getUserByEmail(email);
        if (result?.success && result?.data) {
          return true;
        }
      }

      // Check in localStorage
      const users = JSON.parse(localStorage.getItem('raath_users') || '[]');
      const existing = users.find(u => u.email === email);
      if (existing) return true;

      // Check in localStorage staff
      const staff = JSON.parse(localStorage.getItem('raath_staff') || '[]');
      const existingStaff = staff.find(s => s.email === email);
      if (existingStaff) return true;

      return false;
    } catch (err) {
      console.error('Check email error:', err);
      return false;
    }
  };

  // ✅ FIX: Generate unique shop ID
  const generateShopId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `shop_${timestamp}_${random}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // ============================================================
      // STEP 1: Check if email already exists
      // ============================================================
      const emailExists = await checkEmailExists(formData.email);
      if (emailExists) {
        setError('Email already registered! Please use a different email or login.');
        setLoading(false);
        return;
      }

      // ✅ FIX: Generate unique shop ID for this user
      const shopId = generateShopId();

      const userData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        shopId: shopId,                    // ✅ NEW: Unique shop ID
        shop_name: formData.shop_name,
        shop_address: formData.shop_address,
        business_type: formData.business_type,
        currency: formData.currency,
        role: 'admin',
        status: 'active',
        createdAt: new Date().toISOString(),
      };

      let createdUser = null;

      // ============================================================
      // STEP 2: Try to create via Electron API
      // ============================================================
      if (window.electronAPI?.createUser) {
        const result = await window.electronAPI.createUser(userData);
        if (result?.success) {
          createdUser = { ...userData, id: result.data?.lastInsertRowid || Date.now() };
        } else {
          throw new Error(result?.error || 'Failed to create account');
        }
      }

      // ============================================================
      // STEP 3: Fallback: Save to localStorage
      // ============================================================
      if (!createdUser) {
        const users = JSON.parse(localStorage.getItem('raath_users') || '[]');

        // Double check email (safety)
        const existing = users.find(u => u.email === formData.email);
        if (existing) {
          throw new Error('Email already registered');
        }

        createdUser = { ...userData, id: Date.now() };
        users.push(createdUser);
        localStorage.setItem('raath_users', JSON.stringify(users));

        // Also save as staff
        const staff = JSON.parse(localStorage.getItem('raath_staff') || '[]');
        staff.push({
          id: Date.now(),
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          role: 'admin',
          shopId: shopId,                  // ✅ NEW: Save shopId in staff too
          is_active: true,
          joining_date: new Date().toISOString().split('T')[0],
        });
        localStorage.setItem('raath_staff', JSON.stringify(staff));
      }

      // ✅ FIX: Save shopId to localStorage for cloudSync
      localStorage.setItem('raath_shop_id', shopId);

      if (createdUser) {
        const { password, ...safeUser } = createdUser;

        // ✅ FIX: Save user with shopId to raath_dine_user
        localStorage.setItem('raath_dine_user', JSON.stringify(safeUser));

        if (onSignup) onSignup(safeUser);
        navigate('/');
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ textAlign: 'left' }}>
            <TextField
              fullWidth
              label="Full Name / پورا نام"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              margin="normal"
              autoFocus
              placeholder="Enter your full name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              margin="normal"
              placeholder="you@example.com"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              margin="normal"
              placeholder="03XX-XXXXXXX"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              margin="normal"
              placeholder="Min 6 characters"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              margin="normal"
              placeholder="Repeat password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ textAlign: 'left' }}>
            <TextField
              fullWidth
              label="Shop / Restaurant Name"
              name="shop_name"
              value={formData.shop_name}
              onChange={handleChange}
              required
              margin="normal"
              autoFocus
              placeholder="Your restaurant name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Store color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              label="Shop Address"
              name="shop_address"
              value={formData.shop_address}
              onChange={handleChange}
              margin="normal"
              multiline
              rows={2}
              placeholder="Shop address"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOn color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <FormControl fullWidth margin="normal" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <InputLabel>Business Type</InputLabel>
              <Select
                name="business_type"
                value={formData.business_type}
                onChange={handleChange}
                label="Business Type"
                startAdornment={
                  <InputAdornment position="start">
                    <Business color="action" />
                  </InputAdornment>
                }
              >
                <MenuItem value="restaurant">🍽️ Restaurant</MenuItem>
                <MenuItem value="cafe">☕ Cafe</MenuItem>
                <MenuItem value="fast_food">🍔 Fast Food</MenuItem>
                <MenuItem value="bbq">🔥 BBQ</MenuItem>
                <MenuItem value="bakery">🍞 Bakery</MenuItem>
                <MenuItem value="other">📋 Other</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <InputLabel>Currency</InputLabel>
              <Select
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                label="Currency"
                startAdornment={
                  <InputAdornment position="start">
                    <AttachMoney color="action" />
                  </InputAdornment>
                }
              >
                <MenuItem value="PKR">🇵🇰 PKR (Rs)</MenuItem>
                <MenuItem value="USD">🇺🇸 USD ($)</MenuItem>
                <MenuItem value="EUR">🇪🇺 EUR (€)</MenuItem>
                <MenuItem value="GBP">🇬🇧 GBP (£)</MenuItem>
                <MenuItem value="AED">🇦🇪 AED (د.إ)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Avatar sx={{ width: 60, height: 60, bgcolor: '#4caf50', mx: 'auto', mb: 2 }}>
              <PersonAddIcon sx={{ fontSize: 30 }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Ready to Create Account!
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Please review your details before submitting:
            </Typography>
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2, textAlign: 'left' }}>
              <Typography variant="body2"><strong>Name:</strong> {formData.name}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {formData.email}</Typography>
              <Typography variant="body2"><strong>Shop:</strong> {formData.shop_name}</Typography>
              <Typography variant="body2"><strong>Type:</strong> {formData.business_type}</Typography>
              <Typography variant="body2"><strong>Currency:</strong> {formData.currency}</Typography>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        p: 2,
      }}
    >
      <Fade in timeout={600}>
        <Paper
          elevation={10}
          sx={{
            width: '100%',
            maxWidth: 480,
            p: { xs: 3, sm: 5 },
            borderRadius: 4,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 5,
              background: 'linear-gradient(90deg, #ff6b35, #f7931e)',
            },
          }}
        >
          {/* Logo */}
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: '#ff6b35',
              mx: 'auto',
              mb: 2,
              boxShadow: '0 8px 24px rgba(255,107,53,0.3)',
            }}
          >
            <RestaurantIcon sx={{ fontSize: 40 }} />
          </Avatar>

          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a1a2e', mb: 0.5 }}>
            RAATH DINE
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Create Your Account / اکاؤنٹ بنائیں
          </Typography>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2, textAlign: 'left', borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {/* Step Content */}
          <Box component="form" onSubmit={activeStep === 2 ? handleSubmit : (e) => e.preventDefault()}>
            {renderStepContent()}

            {/* Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              {activeStep > 0 && (
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  startIcon={<ArrowBackIcon />}
                  sx={{ flex: 1, borderRadius: 2, py: 1.2, textTransform: 'none' }}
                >
                  Back
                </Button>
              )}

              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{ 
                    flex: 1, 
                    borderRadius: 2, 
                    py: 1.2, 
                    textTransform: 'none',
                    bgcolor: '#ff6b35',
                    '&:hover': { bgcolor: '#e55a2b' },
                  }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />}
                  sx={{ 
                    flex: 1, 
                    borderRadius: 2, 
                    py: 1.2, 
                    textTransform: 'none',
                    bgcolor: '#4caf50',
                    fontWeight: 700,
                    '&:hover': { bgcolor: '#388e3c' },
                    '&:disabled': { bgcolor: '#ccc' },
                  }}
                >
                  {loading ? 'Creating...' : 'Create Account'}
                </Button>
              )}
            </Box>
          </Box>

          {/* Login Link */}
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 3 }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: '#ff6b35', textDecoration: 'none', fontWeight: 600 }}>
              Sign In / لاگ ان
            </Link>
          </Typography>
        </Paper>
      </Fade>
    </Box>
  );
};

export default Signup;